import { useEffect, useState } from "react";

import { Modal, Stack, Typography } from "@mui/material";

import useAxios from "axios-hooks";

import MainCard from "components/mainCard";
import TextField from "components/textField";
import Button from "components/button";
import Loader from "components/loader";

import { getLocalStorage } from "features/components/helperFunctions";
import { ReservationStatusActionButtonProps } from "features/reservations/index.types";
import { textAreaStyles } from "features/products/index.styles";
import { actionModalSX } from "features/orders/index.styles";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import { dispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";

const ReservationStatusActionButton = ({
  reservationId,
  timeline,
  reservationStatusActionButtonCallback,
}: ReservationStatusActionButtonProps) => {
  const [comment, setComment] = useState<string>("");
  const [toggleStatusModal, setToggleStatusModal] = useState<boolean>(false);
  const [fieldError, setFieldError] = useState<string>("");

  const { eatout_id, user_id } = getLocalStorage();

  //======================================= API Calls =======================================//

  // reservation status update API call payload
  const reservationStatusUpdateAPIPayload = () => {
    const formData = new FormData();

    formData.append("reservation_id", reservationId);
    formData.append("status", timeline.status);
    formData.append("comment", comment);
    formData.append("table_no", "");
    formData.append("invoice_no", "");
    formData.append("amount", "");

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("eatout_uid", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  // reservation status update API call
  const [
    {
      loading: reservationStatusUpdateLoading,
      error: reservationStatusUpdateError,
    },
    reservationStatusUpdateAPICall,
  ] = useAxios(API_END_POINTS.updateReservationStatus, { manual: true });

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    if (reservationStatusUpdateError) {
      // notify user
      dispatch(
        openSnackbar({
          open: true,
          message: reservationStatusUpdateError
            ? reservationStatusUpdateError.message
            : "",
          alert: {
            color: "error",
          },
        })
      );
    }
  }, [reservationStatusUpdateError]);

  //======================================= Handler Functions =======================================//

  const handleFieldChange = (event: { target: { value: string } }) => {
    setComment(event.target.value);

    if (fieldError) {
      setFieldError("");
    }
  };

  const handleUpdateOrderStatus = async () => {
    if (timeline?.status === "Cancel" && !comment) {
      setFieldError("comment");
    } else {
      const { data } = await reservationStatusUpdateAPICall({
        data: reservationStatusUpdateAPIPayload(),
      });

      // status 1 => getting required response from API
      if (data?.status === "1") {
        // close the status confirmation modal
        handleToggleStatusModal();

        // callback to call timeline & reservations api
        reservationStatusActionButtonCallback();

        // notify user
        dispatch(
          openSnackbar({
            open: true,
            message: "Status updated successfully",
            alert: {
              color: "success",
            },
          })
        );
      } else {
        // notify user
        dispatch(
          openSnackbar({
            open: true,
            message: "Failed to update status!",
            alert: {
              color: "error",
            },
          })
        );
      }
    }
  };

  const handleToggleStatusModal = () => {
    setToggleStatusModal((prevState) => !prevState);
  };

  return (
    <>
      {reservationStatusUpdateLoading && <Loader />}

      <Button
        sx={{
          backgroundColor: timeline.color ? timeline.color : "#aaaeb1",
          width: "80px",
          height: " 30px",
          borderRadius: "8px",
        }}
        disabled={timeline.click === "0"}
        onClick={handleToggleStatusModal}
      >
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontStyle: "normal",
            fontWeight: "500",
            fontSize: "13px",
            lineHeight: "15px",
            color: "#673AB7",
          }}
        >
          {timeline.status === "Confirmed" ? "Confirm" : timeline.status}
        </Typography>
      </Button>

      {/* reservation status change modal */}
      <Modal open={toggleStatusModal} onClose={handleToggleStatusModal}>
        <MainCard
          contentSX={{
            "& .MuiCardContent-root": {
              p: "unset !important",
            },
            m: "unset",
            p: "unset !important",
          }}
          sx={actionModalSX}
        >
          <Stack>
            <Stack>
              <Typography variant="h3" sx={{ mb: "48px" }}>
                {timeline?.status === "Cancel"
                  ? "Are you sure you want to cancel the reservation?"
                  : `Are you sure you want to change the status?`}
              </Typography>
            </Stack>

            {timeline?.status === "Cancel" && (
              <Stack mb="8px">
                <TextField
                  label="Reason/comment"
                  value={comment}
                  type="text"
                  rows={2}
                  multiline={true}
                  sx={textAreaStyles}
                  onChange={handleFieldChange}
                  error={fieldError === "comment" ? true : false}
                  helperText={"Required*"}
                />
              </Stack>
            )}

            <Stack direction="row" sx={{ justifyContent: "end" }} spacing={1.5}>
              <Button
                color={"secondary"}
                variant={"contained"}
                sx={{
                  background: "#F5F5F5 ",
                  color: "#212121",
                  p: "12px 57px",

                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                  },
                }}
                onClick={handleToggleStatusModal}
              >
                No
              </Button>

              <Button
                color={"secondary"}
                variant={"contained"}
                sx={{ p: "12px 30px" }}
                onClick={handleUpdateOrderStatus}
              >
                Yes,{" "}
                {timeline.status === "Confirmed" ? "Confirm" : timeline.status}
              </Button>
            </Stack>
          </Stack>
        </MainCard>
      </Modal>
    </>
  );
};

export default ReservationStatusActionButton;
